import { inject, Injectable } from '@angular/core';
import { AuthorizationService } from '../authorization/authorization.service';
import { UserService } from '../user/user.service';
import { CreditSummaryResponse, CustomerService } from '../customer/customer.service';
import { BehaviorSubject, firstValueFrom, interval, Observable } from 'rxjs';
import { NLSTransactionService } from '../nls-transaction/nls-transaction.service';
import { AppConstants } from 'src/app/constants/app-constants';
import { AuthService } from '../auth/auth.service';
import { User } from 'src/app/model/User';

/**
 * Service for managing the borrower credit summary data. Credit summary data is fetched from the backend and is updated
 * whenever the user performs actions. Various services are imported into this service to faciliate data aggregation
 */
@Injectable({
  providedIn: 'root'
})
export class CreditSummaryService {
  /** Dependency injections */
  private _authService = inject(AuthService);
  private _userService = inject(UserService);
  private _customerService = inject(CustomerService);
  private _authorizationService = inject(AuthorizationService);
  private _nlsTransactionService = inject(NLSTransactionService);

  /** Store */
  private _creditSummary: CreditSummaryResponse | null = null;

  /** Observables */
  private _creditLimit = new BehaviorSubject<number | null>(null);
  creditLimit$ = this._creditLimit.asObservable();

  private _creditInUse = new BehaviorSubject<number | null>(null);
  creditInUse$ = this._creditInUse.asObservable();

  private _authorizedHolds = new BehaviorSubject<number | null>(null);
  authorizedHolds$ = this._authorizedHolds.asObservable();

  private _remainingCredit = new BehaviorSubject<number | null>(null);
  remainingCredit$ = this._remainingCredit.asObservable();

  /** Interval to fetch data */
  private _timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);

  /**
   * On construct, fetch the initial credit summary data from the backend
   */
  constructor() {
    // On authentication changes, clear persisted data
    this._userService.user$.subscribe((user: User | null) => {
      if (this._authService.authenticated && user != undefined) {
        this._fetch();
      } else {
        this._clear();
      }
    });

    // Set up periodic refresh
    this._timer.subscribe(() => {
      this._fetch();
    });

    // Subscribe to service changes to update calculated fields
    this._nlsTransactionService.changes$.subscribe(() => {
      this.setCreditInUse(this._creditSummary?.creditInUse ?? null);
      this.setRemainingCredit();
    });

    this._authorizationService.authorizations$.subscribe(() => {
      this.setAuthorizedHolds(this._creditSummary?.authorizedHolds ?? null);
      this.setRemainingCredit();
    });
  }

  /**
   * Fetches the latest credit summary data from the backend
   */
  private async _fetch(): Promise<void> {
    // Get user ID
    const userId = this._userService.user?.id;
    if (!userId) {
      return;
    }

    // Get company id
    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      return;
    }

    // Get credit summary
    const summary = await firstValueFrom(this._customerService.getCustomerCreditSummary$(companyId))
    this._creditSummary = summary;

    // Set values
    await this._set();
  }

  /**
   * Set the entire credit summary at once
   */
  private async _set(): Promise<void> {
    if (!this._creditSummary) {
      return;
    }

    await this.setCreditLimit(this._creditSummary.creditLimit);
    await this.setCreditInUse(this._creditSummary.creditInUse);
    await this.setAuthorizedHolds(this._creditSummary.authorizedHolds);
    await this.setRemainingCredit();
  }

  /**
   * Clear existing data
   */
  private _clear(): void {
    this._creditSummary = null;
    this._creditLimit.next(null);
    this._creditInUse.next(null);
    this._authorizedHolds.next(null);
    this._remainingCredit.next(null);
  }

  /** Specialized setters */

  /**
   * Sets the credit limit value and updates the observable
   * @note This value is only updated from backend data, not frontend actions
   * 
   * @param amount The new credit limit amount
   */
  async setCreditLimit(amount: number | null): Promise<void> {
    this._creditLimit.next(amount);
  }

  /**
   * Sets the credit in use value and updates the observable. This uses nls transactions
   * to calculate the current credit in use
   * 
   * @param apiAmount The new credit in use amount
   */
  async setCreditInUse(apiAmount: number | null): Promise<void> {
    const transactions = this._nlsTransactionService.getEntities();
    
    // If we haven't loaded transactions yet, use the API amount
    if (!transactions || transactions.length === 0) {
      this._creditInUse.next(apiAmount);
      return;
    }
    this._creditInUse.next(apiAmount);
    return;

    // NOTE: temporarily removed, don't know if we want to aggregate from transactions or not (in case transactions don't post)
    // Otherwise, aggregate from transactions
    //const sum = transactions.map(x => x.amount).reduce((a, b) => a + b, 0) ?? 0;
    //this._creditInUse.next(sum);
  }

  /**
   * Sets the authorized holds value and updates the observable. This uses authorizations to 
   * calculate the current authorized holds
   * 
   * @param apiAmount The new authorized holds amount from the API
   */
  async setAuthorizedHolds(apiAmount: number | null): Promise<void> {
    const authorizations = this._authorizationService.getAuthorizations();

    // If we haven't loaded authorizations yet, use the API amount
    if (!authorizations || authorizations.length === 0) {
      this._authorizedHolds.next(apiAmount);
      return;
    }

    // Otherwise, aggregate from authorizations
    const sum = authorizations.map(x => x.remainingAmount).reduce((a, b) => a + b, 0) ?? 0;
    this._authorizedHolds.next(sum);
  }

  /**
   * Sets the remaining credit value and updates the observable. This is calculated from
   * the credit limit, credit in use, and authorized holds after they've been set
   */
  async setRemainingCredit(): Promise<void> {
    const creditLimit = this.creditLimit;
    const creditInUse = this.creditInUse;
    const authorizedHolds = this.authorizedHolds;

    if (creditLimit === null || creditInUse === null || authorizedHolds === null) {
      this._remainingCredit.next(null);
      return;
    }

    this._remainingCredit.next(creditLimit - creditInUse - authorizedHolds);
  }

  /** Getters from behavior subjects */

  get creditLimit(): number | null {
    return this._creditLimit.value;
  }

  get creditInUse(): number | null {
    return this._creditInUse.value;
  }

  get authorizedHolds(): number | null {
    return this._authorizedHolds.value;
  }

  get remainingCredit(): number | null {
    return this._remainingCredit.value;
  }
}
